(function () {
    /********************* Setup *********************/

    // find the section from the script's data-id
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"]`);
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    // product data embedded as JSON in the section liquid
    const product = JSON.parse(section.querySelector(".productV2JSON").textContent);
    const media = JSON.parse(section.querySelector(".productV2MediaJSON").textContent);
    let activeVariant = JSON.parse(section.querySelector(".productV2VariantJSON").textContent);

    // the elements we use over and over
    const optionsElement = section.querySelector(".productV2ContentOptions");
    const atcButton = section.querySelector(".productV2ContentATC");
    const outOfStockElement = section.querySelector(".productV2ContentOutOfStock");
    const slidesElement = section.querySelector(".productV2VisualSlides");
    const paginationElement = section.querySelector(".productV2VisualPaginationList");
    const navPrev = section.querySelector(".productV2VisualNav.prev");
    const navNext = section.querySelector(".productV2VisualNav.next");
    const lightbox = section.querySelector(".productV2Lightbox");
    const lightboxWrapper = lightbox.querySelector(".productV2LightboxSwiperWrapper");
    const lightboxThumbnails = lightbox.querySelector(".productV2LightboxThumbnails");
    const lightboxZoomIn = lightbox.querySelector(".productV2LightboxActionsZoomIn");
    const lightboxZoomOut = lightbox.querySelector(".productV2LightboxActionsZoomOut");
    const gridElement = section.querySelector(".productV2ContentGrid");
    const sizeChart = section.querySelector(".productV2SizeChart");
    const specSheetLink = section.querySelector(".productV2BottomDownloadsItemsItem.specSheet");

    // where each option sits, found by name so the option order in Shopify can change
    const colorIndex = product.options.indexOf("Color");
    const sizeIndex = product.options.indexOf("Size");
    const widthIndex = product.options.indexOf("Width");

    // the active-variant view's response for the current options (each option value's variant, available colors and
    // the color's media), filled in after the first option change
    let activeSelection = null;

    // the media positions the gallery should show, and the ones it is showing
    let mediaPositions = [];
    slidesElement.querySelectorAll(".productV2VisualSlidesItem").forEach(slide => {
        mediaPositions.push(parseInt(slide.dataset.position));
    });
    let shownPositions = mediaPositions;

    // counts option changes so a slow response can tell a newer click has happened since
    let latestOptionChange = 0;

    // active-variant responses by option value ids, so going back to a choice is instant
    const selectionCache = {};

    // B2B grid: the columns for the active color, every variant seen in the grid, and the quantities entered
    let gridColumns = [];
    const gridVariants = {};
    const gridQuantities = {};

    // set once the Back In Stock app has hooked into the button
    let backInStockReady = false;

    // milliseconds in a week, for the B2B "available in N weeks" text
    const WEEK_MS = 604800000;

    /********************* Helpers *********************/

    // escape text going into html strings and attributes
    function escapeHTML(value) {
        return String(value ?? "")
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;");
    }

    /********************* Selection *********************/

    // the active-variant view wants one option value id per option, in option order
    function getOptionValueIDs(optionNames) {
        const ids = [];
        optionNames.forEach((name, index) => {
            const value = product.option_values[index].find(item => item.name === name);
            if (value) {
                ids.push(value.id);
            }
        });
        return ids;
    }

    // load the active-variant view for one combination of options: the variant, each option value's variant, the
    // colors with stock and the color's media. The page never loads every variant, so this is how it learns about them.
    async function fetchSelection(optionNames) {
        const key = getOptionValueIDs(optionNames).join(",");

        // reuse a response we already have
        if (selectionCache[key]) {
            return selectionCache[key];
        }

        const response = await fetch(`${window.location.pathname}?view=active-variant&option_values=${key}`);
        if (!response.ok) {
            throw new Error(`Active variant view returned ${response.status}`);
        }

        const selection = await response.json();
        selectionCache[key] = selection;
        return selection;
    }

    // a size or width that doesn't exist in this color has no variant, so pick the closest one the response lists:
    // the same choice with a different value of another option, in stock first, and never a different color
    function findClosestVariant(selection, changedIndex) {
        const candidates = [];
        selection.options.forEach((option, index) => {
            // keep the value the shopper just picked, and keep the color
            if (index === changedIndex || index === colorIndex) {
                return;
            }

            option.values.forEach(value => {
                if (value.variant) {
                    candidates.push(value.variant);
                }
            });
        });

        // prefer one in stock
        const availableCandidate = candidates.find(variant => variant.available);
        if (availableCandidate) {
            return availableCandidate;
        }

        if (candidates.length > 0) {
            return candidates[0];
        }

        return null;
    }

    /********************* Options *********************/

    // the value of each option: the checked input, or the active variant's value for options without inputs (B2B only
    // shows the color)
    function getSelectedOptions() {
        // the page can load on a combination that doesn't exist, which leaves no active variant to start from
        let selectedOptions = [];
        if (activeVariant) {
            selectedOptions = [...activeVariant.options];
        }

        section.querySelectorAll(".productV2ContentOptionsItem").forEach(fieldset => {
            const input = fieldset.querySelector("input:checked");
            if (!input) {
                return;
            }
            selectedOptions[parseInt(fieldset.dataset.index)] = input.value;
        });
        return selectedOptions;
    }

    // check the inputs and fill the labels for the active variant
    function syncOptionInputs() {
        section.querySelectorAll(".productV2ContentOptionsItem").forEach(fieldset => {
            const value = activeVariant.options[parseInt(fieldset.dataset.index)];
            fieldset.querySelectorAll("input").forEach(input => {
                input.checked = input.value === value;
            });
            fieldset.querySelector(".productV2ContentOptionsItemLabelValue").textContent = value;
        });
    }

    // a size or width is unavailable when its variant (with the other selected values) is missing or sold out
    function updateOptionAvailability() {
        section.querySelectorAll(".productV2ContentOptionsItemValuesItem").forEach(label => {
            const input = label.querySelector("input");
            const optionIndex = parseInt(input.closest(".productV2ContentOptionsItem").dataset.index);
            const value = activeSelection.options[optionIndex].values.find(item => item.name === input.value);

            let isAvailable = false;
            if (value && value.variant && value.variant.available) {
                isAvailable = true;
            }
            label.classList.toggle("unavailable", !isAvailable);
        });
    }

    // retail marks colors with nothing in stock, but never the color being viewed
    function updateColorAvailability() {
        if (product.is_b2b) {
            return;
        }

        section.querySelectorAll(".productV2ContentOptionsItemSwatchesItem").forEach(label => {
            const input = label.querySelector("input");
            const isAvailable = activeSelection.available_colors.includes(`|${input.value}|`);
            label.classList.toggle("soldOut", !isAvailable && !input.checked);
        });
    }

    // a shopper picked a color, size or width
    async function selectOption(e) {
        const fieldset = e.target.closest(".productV2ContentOptionsItem");
        if (!fieldset) {
            return;
        }

        // remember which change this is, so a slower earlier response can't overwrite a newer click
        latestOptionChange++;
        const thisOptionChange = latestOptionChange;

        let selection = null;
        try {
            // load the variant for the chosen options
            selection = await fetchSelection(getSelectedOptions());

            // that combination may not exist, so move to the closest variant that does
            if (!selection.active_variant) {
                const closestVariant = findClosestVariant(selection, parseInt(fieldset.dataset.index));
                selection = null;
                if (closestVariant) {
                    selection = await fetchSelection(closestVariant.options);
                }
            }
        } catch (error) {
            console.warn("Product option could not be loaded.", error);
            selection = null;
        }

        // the shopper has clicked again since, so that click wins
        if (thisOptionChange !== latestOptionChange) {
            return;
        }

        // nothing to move to (or the request failed), so put the inputs back on the active variant
        if (!selection) {
            syncOptionInputs();
            return;
        }

        activeSelection = selection;
        activeVariant = selection.active_variant;
        mediaPositions = selection.media_positions;

        // the B2B grid lists the active color's sizes, so a color change needs its columns first
        if (product.is_b2b) {
            try {
                await loadGrid();
            } catch (error) {
                console.warn("Product sizes could not be loaded.", error);
                gridColumns = [];
            }
        }

        updateProduct();
    }

    optionsElement?.addEventListener("change", selectOption);

    /********************* Update Product *********************/

    // repaint everything that depends on the active variant
    function updateProduct() {
        syncOptionInputs();
        updateOptionAvailability();
        updateColorAvailability();
        renderGallery();
        updatePrice();

        if (product.is_b2b) {
            renderGrid();
            updateSpecSheetLink();
        } else {
            updateRetail();
        }

        // other sections (style number) listen for variant changes
        document.dispatchEvent(new CustomEvent("activeVariantChange", {
            detail: {
                variant: activeVariant,
                styleNumber: activeVariant.metafields?.style_number,
            },
        }));

        // keep the selected variant in the url so it survives a refresh
        const url = new URL(window.location.href);
        url.searchParams.set("variant", activeVariant.id);
        url.searchParams.delete("option_values");
        window.history.replaceState({}, "", url);
    }

    /********************* Gallery *********************/

    // rebuild the gallery, pagination, lightbox and thumbnails for the active color
    function renderGallery() {
        // only rebuild when the color's images change, so picking a size never moves the gallery
        if (mediaPositions.join() === shownPositions.join()) {
            return;
        }
        shownPositions = mediaPositions;

        let slidesHTML = "";
        let paginationHTML = "";
        let lightboxHTML = "";
        let thumbnailsHTML = "";
        const shownMedia = media.filter(item => mediaPositions.includes(item.position));
        shownMedia.forEach((item, index) => {
            const alt = escapeHTML(item.alt);

            // the second and third images sit side by side on desktop
            let slideClass = "";
            if (index === 1 || index === 2) {
                slideClass = " half";
            }

            // the first slide starts active
            let activeClass = "";
            if (index === 0) {
                activeClass = " active";
            }

            // videos show a play icon on the slide and thumbnail, and play in the lightbox
            let slidePlayHTML = "";
            let thumbnailPlayHTML = "";
            let lightboxContentHTML = `
                <div class="swiper-zoom-container">
                    <img class="productV2LightboxSwiperSlideImage" src="${item.lightbox_image}" alt="${alt}" width="${item.width}" height="${item.height}" loading="lazy">
                </div>
            `;
            if (item.video_html) {
                slidePlayHTML = `<img class="productV2VisualSlidesItemPlay" src="${product.play_icon}" alt="" aria-hidden="true" width="64" height="64">`;
                thumbnailPlayHTML = `<img class="productV2LightboxThumbnailsItemPlay" src="${product.play_icon}" alt="" aria-hidden="true" width="24" height="24">`;
                lightboxContentHTML = item.video_html;
            }

            slidesHTML += `
                <div class="productV2VisualSlidesItem${slideClass}" data-index="${index}" data-position="${item.position}">
                    <img class="productV2VisualSlidesItemImage" src="${item.image}" alt="${alt}" width="${item.width}" height="${item.height}">
                    ${slidePlayHTML}
                </div>
            `;

            paginationHTML += `
                <li class="productV2VisualPaginationListItem${activeClass}" data-index="${index}" role="button" aria-label="Go to slide ${index + 1}"></li>
            `;

            lightboxHTML += `
                <div class="productV2LightboxSwiperSlide swiper-slide" data-index="${index}" data-media-type="${item.type}">
                    ${lightboxContentHTML}
                </div>
            `;

            thumbnailsHTML += `
                <li class="productV2LightboxThumbnailsItem${activeClass}" data-index="${index}" role="button" aria-label="Go to slide ${index + 1}">
                    <img class="productV2LightboxThumbnailsItemImage" src="${item.thumbnail}" alt="${alt}" width="${item.width}" height="${item.height}" loading="lazy">
                    ${thumbnailPlayHTML}
                </li>
            `;
        });

        slidesElement.innerHTML = slidesHTML;
        paginationElement.innerHTML = paginationHTML;
        lightboxWrapper.innerHTML = lightboxHTML;
        lightboxThumbnails.innerHTML = thumbnailsHTML;

        // start the new color from its first image
        lightboxSwiper.update();
        lightboxSwiper.slideTo(0, 0);
        slidesElement.scrollTo({left: 0});
        updateGalleryControls();
    }

    // on mobile the slides scroll sideways one full width at a time
    function getActiveSlideIndex() {
        if (!slidesElement.clientWidth) {
            return 0;
        }
        return Math.round(slidesElement.scrollLeft / slidesElement.clientWidth);
    }

    // show the arrows that can move, and fill the pagination up to the current slide
    function updateGalleryControls() {
        const activeIndex = getActiveSlideIndex();
        const slideCount = slidesElement.querySelectorAll(".productV2VisualSlidesItem").length;

        navPrev.classList.toggle("active", activeIndex > 0);
        navNext.classList.toggle("active", activeIndex < slideCount - 1);
        paginationElement.querySelectorAll(".productV2VisualPaginationListItem").forEach(item => {
            item.classList.toggle("active", parseInt(item.dataset.index) <= activeIndex);
        });
    }

    function scrollToSlide(index) {
        slidesElement.scrollTo({left: index * slidesElement.clientWidth});
    }

    function showPreviousSlide() {
        scrollToSlide(getActiveSlideIndex() - 1);
    }

    function showNextSlide() {
        scrollToSlide(getActiveSlideIndex() + 1);
    }

    // one handler for every pagination item
    function selectPaginationItem(e) {
        const item = e.target.closest(".productV2VisualPaginationListItem");
        if (!item) {
            return;
        }
        scrollToSlide(parseInt(item.dataset.index));
    }

    // open the lightbox on the clicked slide
    function openSlideInLightbox(e) {
        const slide = e.target.closest(".productV2VisualSlidesItem");
        if (!slide) {
            return;
        }
        lightboxSwiper.slideTo(parseInt(slide.dataset.index), 0);
        openLightbox();
    }

    slidesElement.addEventListener("scrollend", updateGalleryControls);
    window.addEventListener("resize", updateGalleryControls);
    navPrev.addEventListener("click", showPreviousSlide);
    navNext.addEventListener("click", showNextSlide);
    paginationElement.addEventListener("click", selectPaginationItem);
    slidesElement.addEventListener("click", openSlideInLightbox);
    updateGalleryControls();

    /********************* Lightbox *********************/

    const lightboxSwiper = new Swiper(lightbox.querySelector(".productV2LightboxSwiper"), {
        centeredSlides: true,
        slidesPerView: 1,
        navigation: {
            prevEl: lightbox.querySelector(".productV2LightboxSwiperPrev"),
            nextEl: lightbox.querySelector(".productV2LightboxSwiperNext"),
        },
    });

    // a video keeps playing off-screen unless it is stopped on the way out
    function pauseVideos() {
        lightbox.querySelectorAll(".productV2LightboxSwiperSlideVideo").forEach(video => {
            video.pause();
        });
    }

    // zoom and video can't share a slide, so the zoom controls only show on images
    function updateZoomButtons() {
        const activeSlide = lightboxSwiper.slides[lightboxSwiper.activeIndex];

        let isImage = true;
        if (activeSlide && activeSlide.dataset.mediaType === "video") {
            isImage = false;
        }

        lightboxZoomIn.classList.toggle("active", isImage);
        lightboxZoomOut.classList.toggle("active", isImage);
    }

    function openLightbox() {
        lightbox.showModal();
        document.body.classList.add("noScroll");
        updateZoomButtons();
    }

    function closeLightbox() {
        lightbox.close();
    }

    // reset zoom, stop any video and move the thumbnail highlight with the slide
    function changeLightboxSlide() {
        if (lightboxSwiper.zoom.enabled) {
            lightboxSwiper.zoom.out();
        }

        pauseVideos();

        lightboxZoomIn.disabled = false;
        lightboxZoomOut.disabled = true;
        updateZoomButtons();

        lightboxThumbnails.querySelectorAll(".productV2LightboxThumbnailsItem").forEach(item => {
            item.classList.toggle("active", parseInt(item.dataset.index) === lightboxSwiper.realIndex);
        });
    }

    // one handler for every thumbnail
    function selectThumbnail(e) {
        const item = e.target.closest(".productV2LightboxThumbnailsItem");
        if (!item) {
            return;
        }
        lightboxSwiper.slideTo(parseInt(item.dataset.index));
    }

    // the dialog also closes on esc, so clean up on the native close event
    function cleanUpLightbox() {
        pauseVideos();
        document.body.classList.remove("noScroll");
    }

    function zoomIn() {
        lightboxSwiper.zoom.enable();
        lightboxSwiper.zoom.in(1.5);
        lightboxZoomOut.disabled = false;
        lightboxZoomIn.disabled = true;
    }

    function zoomOut() {
        lightboxSwiper.zoom.out();
        lightboxSwiper.zoom.disable();
        lightboxZoomIn.disabled = false;
        lightboxZoomOut.disabled = true;
    }

    lightboxSwiper.on("slideChange", changeLightboxSlide);
    lightboxThumbnails.addEventListener("click", selectThumbnail);
    lightbox.querySelector(".productV2LightboxActionsClose").addEventListener("click", closeLightbox);
    lightbox.addEventListener("close", cleanUpLightbox);
    lightboxZoomIn.addEventListener("click", zoomIn);
    lightboxZoomOut.addEventListener("click", zoomOut);
    updateZoomButtons();

    /********************* Description *********************/

    function showFullDescription() {
        section.querySelector(".productV2ContentDescriptionFull").classList.add("active");
        section.querySelector(".productV2ContentDescriptionTruncated").classList.remove("active");
    }

    function showTruncatedDescription() {
        section.querySelector(".productV2ContentDescriptionFull").classList.remove("active");
        section.querySelector(".productV2ContentDescriptionTruncated").classList.add("active");
    }

    section.querySelector(".productV2ContentDescriptionTruncatedButton").addEventListener("click", showFullDescription);
    section.querySelector(".productV2ContentDescriptionFullButton").addEventListener("click", showTruncatedDescription);

    /********************* Price *********************/

    // colors share one product, so the price and the clearance badge follow the active variant
    function updatePrice() {
        const priceReal = section.querySelector(".productV2ContentPriceReal");
        const priceCompare = section.querySelector(".productV2ContentPriceCompare");

        // retail prices are cents on the variant, B2B prices are dollars in metafields
        let realAmount = activeVariant.price;
        let compareAmount = activeVariant.compare_at_price;
        if (product.is_b2b) {
            realAmount = Math.round((activeVariant.metafields?.b2b_price || 0) * 100);
            compareAmount = Math.round((activeVariant.metafields?.b2b_compare_at_price || 0) * 100);
        }

        // show the compare price only when it's a sale
        const isSale = compareAmount > realAmount;
        priceReal.textContent = price(realAmount);
        priceReal.classList.toggle("sale", isSale);
        priceCompare.textContent = "";
        if (isSale) {
            priceCompare.textContent = price(compareAmount);
        }

        // clearance is a retail markdown on both sites, matching the B2B clearance order check
        const isClearance = activeVariant.compare_at_price > activeVariant.price;
        section.querySelector(".productV2VisualBadgesItem.sale").classList.toggle("active", isClearance);

        // a custom badge gives way to clearance, and only some products have one
        section.querySelector(".productV2VisualBadgesItem.custom")?.classList.toggle("active", !isClearance);

        // new when the variant is, or any variant in its color
        let isNew = activeVariant.metafields.is_new === true;
        if (colorIndex >= 0 && product.new_colors.includes(activeVariant.options[colorIndex])) {
            isNew = true;
        }

        // clearance wins when the variant is both
        if (isClearance) {
            isNew = false;
        }
        section.querySelector(".productV2VisualBadgesItem.new").classList.toggle("active", isNew);
    }

    /********************* Retail *********************/

    // add to cart state for the active variant
    function updateRetail() {
        atcButton.disabled = !activeVariant.available;
        atcButton.classList.toggle("primary", activeVariant.available);
        atcButton.textContent = "Sold Out";
        if (activeVariant.available) {
            atcButton.textContent = "Add To Cart";
        }

        // the Back In Stock button follows the active variant once the app is ready
        if (backInStockReady) {
            BIS.refreshInlineButton();
        }
    }

    async function addToCart() {
        atcButton.classList.add("loading");

        // send the category with the line item when the product has one
        const item = {
            id: activeVariant.id,
            quantity: 1,
        };
        if (product.category) {
            item.properties = {
                "Category": product.category,
            };
        }

        await addProduct({items: [item]});

        atcButton.classList.remove("loading");
        await openCart();
    }

    if (!product.is_b2b) {
        atcButton.addEventListener("click", addToCart);
    }

    /********************* Size Chart *********************/

    // the table header follows the dialog's scroll by hand, because the table scrolls sideways and that breaks
    // position: sticky
    function updateSizeChartHeader() {
        const OFFSET = -28;
        const scrollContainer = sizeChart.querySelector(".productV2SizeChartInner");
        const tableWrapper = sizeChart.querySelector(".productV2SizeChartTableWrapper");
        const tableHeader = sizeChart.querySelector(".productV2SizeChartTableHead");

        const startSticky = tableWrapper.offsetTop - OFFSET;
        const stopSticky = tableWrapper.offsetTop + tableWrapper.offsetHeight - tableHeader.offsetHeight - OFFSET;
        const scrollTop = scrollContainer.scrollTop;

        // before the table: no offset; on the table: follow the scroll; past the table: stop at its bottom
        let translateY = 0;
        if (scrollTop >= startSticky && scrollTop <= stopSticky) {
            translateY = scrollTop - tableWrapper.offsetTop + OFFSET;
        } else if (scrollTop > stopSticky) {
            translateY = tableWrapper.offsetHeight - tableHeader.offsetHeight;
        }
        tableHeader.style.transform = `translateY(${translateY}px)`;
    }

    function openSizeChart() {
        sizeChart.showModal();
        document.body.classList.add("noScroll");
    }

    function closeSizeChart() {
        sizeChart.close();
    }

    // the dialog also closes on esc, so clean up on the native close event
    function cleanUpSizeChart() {
        document.body.classList.remove("noScroll");
    }

    // only retail has a size chart
    if (sizeChart) {
        sizeChart.querySelector(".productV2SizeChartInner").addEventListener("scroll", updateSizeChartHeader);
        sizeChart.querySelector(".productV2SizeChartHeaderClose").addEventListener("click", closeSizeChart);
        sizeChart.addEventListener("close", cleanUpSizeChart);
        section.querySelector(".productV2ContentOptionsItemSizeButton")?.addEventListener("click", openSizeChart);
    }

    /********************* B2B Grid *********************/

    // stock status for one variant, matching the legend colors
    function getGridStatus(variant) {
        const metafields = variant.metafields || {};
        const inventory = metafields.b2b_inventory_quantity || 0;
        const now = new Date();

        let availableDate = null;
        if (metafields.b2b_available_date) {
            availableDate = new Date(metafields.b2b_available_date);
        }

        // future dates win because future items also carry a quantity
        if (availableDate && availableDate > now) {
            const weeks = Math.floor((availableDate - now) / WEEK_MS);
            const dateText = availableDate.toLocaleDateString("en-US", {month: "short", day: "2-digit", year: "numeric", timeZone: "UTC"});

            let weeksLabel = "weeks";
            if (weeks === 1) {
                weeksLabel = "week";
            }

            let inventoryHTML = "";
            if (inventory > 0) {
                inventoryHTML = `${inventory} available in<br>`;
            }

            return {
                status: "future",
                html: `${inventoryHTML}${weeks} ${weeksLabel}<br>${dateText}`,
            };
        }

        if (inventory > 0) {
            return {
                status: "available",
                html: `${inventory} in stock`,
            };
        }

        return {
            status: "unavailable",
            html: "Delivery TBD",
        };
    }

    // the grid lists every size and width of the active color. One active-variant response lists the sizes for one
    // width, so a product with widths needs one request per width.
    async function loadGrid() {
        // the widths to request; a product without a Width option needs just one request
        let widths = [null];
        if (widthIndex !== -1) {
            widths = product.option_values[widthIndex].map(value => value.name);
        }

        // request the active color and size in each width
        const requests = [];
        widths.forEach(width => {
            const optionNames = [...activeVariant.options];
            if (width !== null) {
                optionNames[widthIndex] = width;
            }
            requests.push(fetchSelection(optionNames));
        });
        const selections = await Promise.all(requests);

        // one column per size and width that exists, in the order the responses list them
        const columns = [];
        selections.forEach(selection => {
            // each Size value's variant is that size in this color and width; without sizes the grid is just the variant
            let sizeVariants = [selection.active_variant];
            if (sizeIndex !== -1) {
                sizeVariants = selection.options[sizeIndex].values.map(value => value.variant);
            }

            sizeVariants.forEach(variant => {
                if (!variant || columns.some(column => column.id === variant.id)) {
                    return;
                }
                columns.push(variant);

                // keep every variant seen, so quantities entered on other colors can still be added to the cart
                gridVariants[variant.id] = variant;
            });
        });
        gridColumns = columns;
    }

    // build the active color's columns, keeping any quantities already entered
    function renderGrid() {
        let html = "";
        gridColumns.forEach(variant => {
            const gridStatus = getGridStatus(variant);

            let size = variant.options[0];
            if (sizeIndex !== -1) {
                size = variant.options[sizeIndex];
            }

            let width = "";
            if (widthIndex !== -1) {
                width = variant.options[widthIndex];
            }

            let disabledAttribute = "";
            if (gridStatus.status === "unavailable") {
                disabledAttribute = "disabled";
            }

            let quantityValue = "";
            if (gridQuantities[variant.id] > 0) {
                quantityValue = gridQuantities[variant.id];
            }

            html += `
                <div class="productV2ContentGridColumn" data-width="${escapeHTML(width)}" data-style="${escapeHTML(variant.metafields?.style_number)}" data-sku="${escapeHTML(variant.sku)}">
                    <div class="productV2ContentGridColumnHeader">
                        <div class="productV2ContentGridColumnHeaderSize">${escapeHTML(size)}</div>
                        <div class="productV2ContentGridColumnHeaderWidth">${escapeHTML(width)}</div>
                    </div>
                    <div class="productV2ContentGridColumnBody">
                        <button class="productV2ContentGridColumnBodyMinus" type="button" tabindex="-1" ${disabledAttribute}>-</button>
                        <input class="productV2ContentGridColumnBodyInput ${gridStatus.status}" type="number" min="0" data-variant-id="${variant.id}" value="${quantityValue}" aria-label="Quantity for size ${escapeHTML(size)} ${escapeHTML(width)}" ${disabledAttribute}>
                        <button class="productV2ContentGridColumnBodyPlus" type="button" tabindex="-1" ${disabledAttribute}>+</button>
                    </div>
                    <div class="productV2ContentGridColumnStatus">${gridStatus.html}</div>
                </div>
            `;
        });

        // a failed request leaves no columns, so say so rather than show an empty grid
        if (html === "") {
            html = `<div class="productV2ContentGridLoading">Sizes could not be loaded. Please refresh the page.</div>`;
        }

        gridElement.innerHTML = html;

        // tooltips are built from the data attributes so a sku or style number can never be read as html
        tippy(gridElement.querySelectorAll(".productV2ContentGridColumn"), {
            theme: "light",
            allowHTML: true,
            interactive: true,
            content: (column) => `Style: ${escapeHTML(column.dataset.style)} <br> SKU: ${escapeHTML(column.dataset.sku)}`,
        });
    }

    // the total on the add to cart button, and a count on each color's swatch
    function updateGridTotals() {
        const colorTotals = {};
        let total = 0;
        Object.keys(gridQuantities).forEach(variantID => {
            const quantity = gridQuantities[variantID];
            const color = gridVariants[variantID]?.options[colorIndex];

            if (!colorTotals[color]) {
                colorTotals[color] = 0;
            }
            colorTotals[color] += quantity;
            total += quantity;
        });

        section.querySelectorAll(".productV2ContentOptionsItemSwatchesItem").forEach(label => {
            const count = colorTotals[label.querySelector("input").value] || 0;
            const countElement = label.querySelector(".productV2ContentOptionsItemSwatchesItemCount");
            countElement.textContent = count;
            countElement.classList.toggle("active", count > 0);
        });

        atcButton.disabled = total === 0;
        atcButton.classList.toggle("primary", total > 0);
        atcButton.textContent = "Empty Cart";
        if (total > 0) {
            atcButton.textContent = `Add To Cart (${total})`;
        }
    }

    // one handler for every -/+ button
    function changeGridQuantity(e) {
        const button = e.target.closest(".productV2ContentGridColumnBodyMinus, .productV2ContentGridColumnBodyPlus");
        if (!button) {
            return;
        }

        const input = button.closest(".productV2ContentGridColumn").querySelector(".productV2ContentGridColumnBodyInput");
        let quantity = input.valueAsNumber || 0;
        if (button.classList.contains("productV2ContentGridColumnBodyPlus")) {
            quantity++;
        } else if (quantity > 0) {
            quantity--;
        }

        input.value = quantity;
        gridQuantities[input.dataset.variantId] = quantity;
        updateGridTotals();
    }

    // a quantity typed into an input
    function typeGridQuantity(e) {
        if (!e.target.classList.contains("productV2ContentGridColumnBodyInput")) {
            return;
        }
        gridQuantities[e.target.dataset.variantId] = Math.max(0, e.target.valueAsNumber || 0);
        updateGridTotals();
    }

    async function addGridToCart() {
        atcButton.classList.add("loading");

        // every variant with a quantity, across all colors
        const selectedVariants = [];
        Object.keys(gridQuantities).forEach(variantID => {
            if (gridQuantities[variantID] > 0) {
                selectedVariants.push(gridVariants[variantID]);
            }
        });

        // the b2b cart holds at most 250 unique variants, so check before adding any of them
        const newVariantCount = selectedVariants.filter(variant => !b2b.cart[variant.id]).length;
        if (Object.keys(b2b.cart).length + newVariantCount > 250) {
            atcButton.classList.remove("loading");
            await Swal.fire({
                title: "Cart limit reached",
                text: "You can only have 250 unique items in a cart. Please submit or remove items before adding more.",
                icon: "error",
            });
            return;
        }

        // clearance and regular items can't share an order, either within this selection or with the cart
        let hasClearance = false;
        let hasRegular = false;
        selectedVariants.forEach(variant => {
            if (variant.compare_at_price > variant.price) {
                hasClearance = true;
            } else {
                hasRegular = true;
            }
        });

        let errorText = "";
        if (hasClearance && hasRegular) {
            errorText = "You cannot mix clearance and non-clearance items in the same order. Please add them in separate orders.";
        } else if (b2b.getCartQuantity() > 0 && hasClearance !== await b2b.isClearanceOrder()) {
            errorText = "You cannot mix clearance and non-clearance items in the same order. Please clear your cart if you want to add this item.";
        }

        if (errorText) {
            atcButton.classList.remove("loading");
            await Swal.fire({
                title: "Error",
                text: errorText,
                icon: "error",
            });
            return;
        }

        selectedVariants.forEach(variant => {
            b2b.addProduct(variant.id, gridQuantities[variant.id]);
        });

        // clear the entered quantities so a second click can't add them again
        selectedVariants.forEach(variant => {
            delete gridQuantities[variant.id];
        });
        renderGrid();
        updateGridTotals();

        atcButton.classList.remove("loading");

        // continue shopping or go to the b2b cart
        const response = await Swal.fire({
            title: "Product(s) added to cart",
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Go To Cart",
            cancelButtonText: "Continue Shopping",
        });

        if (response.isConfirmed) {
            window.location.href = "/cart?view=b2b";
        }
    }

    if (product.is_b2b) {
        gridElement.addEventListener("click", changeGridQuantity);
        gridElement.addEventListener("input", typeGridQuantity);
        atcButton.addEventListener("click", addGridToCart);
    }

    /********************* Downloads *********************/

    // the spec sheet shows the active color's images
    function updateSpecSheetLink() {
        // products without a color option have no swatches
        const swatchInput = section.querySelector(".productV2ContentOptionsItemSwatchesItemInput:checked");
        if (!swatchInput) {
            return;
        }

        const url = new URL(specSheetLink.href);
        url.searchParams.set("color_id", swatchInput.dataset.id);
        specSheetLink.href = url.toString();
    }

    /********************* Details *********************/

    // open and close each details dropdown
    section.querySelectorAll(".productV2BottomDetailsItem").forEach(item => {
        item.querySelector(".productV2BottomDetailsItemButton").addEventListener("click", () => {
            item.classList.toggle("open");
        });
    });

    // technology logos explain themselves on hover
    tippy(section.querySelectorAll(".productV2BottomDetailsItemLogosItem[data-tooltip]"), {
        theme: "light",
        content: (logo) => logo.dataset.tooltip,
    });

    /********************* Back In Stock *********************/

    // the Back In Stock app hooks into its own button once its popup is ready
    function initBackInStock() {
        if (!window.BIS || product.tags.includes("bis-hidden")) {
            return;
        }

        BIS.popup.ready.then(() => {
            // nothing to subscribe to when every variant is in stock
            if (BIS.popup.variants.length < 1) {
                return;
            }

            const button = outOfStockElement.querySelector(".productV2ContentOutOfStockButton");

            // B2B opens the popup for the whole product
            if (product.is_b2b) {
                outOfStockElement.classList.add("active");
                button.addEventListener("click", () => {
                    BIS.popup.toggle();
                });
                return;
            }

            // retail shows the button only while the active variant is sold out
            button.id = "BIS_trigger";
            button.classList.add("product-submit", "action-button", "submit");
            BIS.inlineButtonAnchor = `[id="${sectionID}"] .productV2ContentOutOfStock`;

            BIS.refreshInlineButton = function () {
                outOfStockElement.classList.toggle("active", !activeVariant.available);
            };
            backInStockReady = true;
            BIS.refreshInlineButton();

            button.addEventListener("click", () => {
                BIS.popup.form.selectVariant(activeVariant.id);
            });
        });
    }

    document.addEventListener("DOMContentLoaded", initBackInStock);

    /********************* Init *********************/

    // retail is rendered in liquid for the selected variant, so only the B2B grid needs loading
    (async () => {
        if (!product.is_b2b || !activeVariant) {
            return;
        }

        try {
            await loadGrid();
        } catch (error) {
            console.warn("Product sizes could not be loaded.", error);
            gridElement.innerHTML = `<div class="productV2ContentGridLoading">Sizes could not be loaded. Please refresh the page.</div>`;
            return;
        }

        renderGrid();
        updateGridTotals();
    })();
}());
